import { type FormEvent, type ReactNode, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { resetPassword, signIn, triggerHaptic } from '@repo/shared/client';
import { errorTracker } from '@repo/shared/common';
import { AlertCircle } from '~/components/icons';
import { sessionQueryKey } from '~/lib/queries/session';
import {
  Alert,
  Anchor,
  Button,
  PasswordInput,
  Reveal,
  Stack,
  Text,
  Title,
} from '~/components/ui';

// The reset email deep-links here with the single-use `token` and the account
// `email` (so we can auto-sign-in after the reset). Both are optional in the
// schema so a malformed/missing-token link renders the invalid state instead of
// throwing.
const ResetPasswordSearchSchema = z.object({
  token: z.string().min(1).optional().catch(undefined),
  email: z.string().email().optional().catch(undefined),
});
type ResetPasswordSearch = z.infer<typeof ResetPasswordSearchSchema>;

// Mirror BetterAuth's default minimum (auth.ts doesn't override it).
const MIN_PASSWORD_LENGTH = 8;

// After a successful reset + auto-sign-in, land on the same post-auth seam the
// sign-in/up flows use.
const POST_SIGNIN_REDIRECT = '/post-sign-in';

export const Route = createFileRoute('/_auth/reset-password')({
  validateSearch: (search): ResetPasswordSearch => ResetPasswordSearchSchema.parse(search),
  component: ResetPasswordRoute,
});

function ResetPasswordRoute(): ReactNode {
  const { t } = useTranslation();
  const { token, email } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirm?: string }>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function submitAsync(): Promise<void> {
    triggerHaptic();
    setError('');
    if (!token) return;

    const errors: { password?: string; confirm?: string } = {};
    if (password.length < MIN_PASSWORD_LENGTH) {
      errors.password = t('auth.passwordTooShort');
    }
    if (confirm !== password) {
      errors.confirm = t('auth.passwordsMismatch');
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      const { error: resetError } = await resetPassword({ token, newPassword: password });
      if (resetError) {
        const code =
          'code' in resetError && typeof resetError.code === 'string' ? resetError.code : 'unknown';
        errorTracker.logMessage('reset-password error', { code }, 'info');
        // An invalid/expired token is the common failure — send them back to
        // request a fresh link.
        setError(
          code === 'INVALID_TOKEN'
            ? t('auth.resetPasswordInvalidToken')
            : t('auth.resetPasswordError'),
        );
        setLoading(false);
        return;
      }

      // Auto-login: the reset revoked all sessions, so sign in fresh with the new
      // password. We have the email from the deep link. If it's somehow missing,
      // fall back to the sign-in screen with the email prefilled.
      if (email) {
        const { error: signInError } = await signIn.email({ email, password });
        if (!signInError) {
          queryClient.removeQueries({ queryKey: sessionQueryKey });
          void navigate({ to: POST_SIGNIN_REDIRECT, replace: true });
          // Leave `loading` true — the navigation unmounts this form.
          return;
        }
      }
      // Reset succeeded but we couldn't auto-login — send them to sign in.
      void navigate({
        to: '/sign-in',
        search: email ? { email } : {},
        replace: true,
      });
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      errorTracker.captureException(wrapped, { context: 'resetPassword' });
      setError(t('auth.resetPasswordError'));
      setLoading(false);
    }
  }

  // No token → the link is malformed or was opened without the query string.
  if (!token) {
    return (
      <Reveal variant="fade">
        <Stack gap="xl">
          <div>
            <Title order={1}>{t('auth.resetPasswordTitle')}</Title>
            <Text c="dimmed" size="sm" mt="xs">
              {t('auth.resetPasswordSubtitle')}
            </Text>
          </div>
          <Alert icon={<AlertCircle size={16} />} color="red" variant="light" data-testid="reset-password-invalid">
            {t('auth.resetPasswordInvalidToken')}
          </Alert>
          <Text ta="center" size="sm">
            <Anchor component={Link} to="/forgot-password" size="sm" onClick={() => triggerHaptic()}>
              {t('auth.forgotPasswordSubmit')}
            </Anchor>
          </Text>
        </Stack>
      </Reveal>
    );
  }

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void submitAsync();
      }}
      noValidate
    >
      <Reveal variant="fade">
        <Stack gap="xl">
          <div>
            <Title order={1}>{t('auth.resetPasswordTitle')}</Title>
            <Text c="dimmed" size="sm" mt="xs">
              {t('auth.resetPasswordSubtitle')}
            </Text>
          </div>

          {error && (
            <Alert icon={<AlertCircle size={16} />} color="red" variant="light" data-testid="reset-password-error">
              {error}
            </Alert>
          )}

          <Stack gap="sm">
            <PasswordInput
              label={t('auth.newPasswordLabel')}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              error={fieldErrors.password}
              description={t('auth.passwordHint')}
              autoComplete="new-password"
              data-testid="reset-password-new"
            />
            <PasswordInput
              label={t('auth.confirmPasswordLabel')}
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
              error={fieldErrors.confirm}
              autoComplete="new-password"
              data-testid="reset-password-confirm"
            />
          </Stack>

          <Button type="submit" size="lg" fullWidth loading={loading} data-testid="reset-password-submit">
            {t('auth.resetPasswordSubmit')}
          </Button>
        </Stack>
      </Reveal>
    </form>
  );
}
